import type { FormApi } from 'final-form';
import type { PropsWithChildren } from 'react';
import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3 } from '../../../components/Button/ButtonV3';
import { SubmitButtonV3 } from '../../../components/FinalForm/SubmitButtonV3';
import { GraphModal } from '../../../components/GraphModal/GraphModal';
import { confirmationToast, failureToast } from '../../../components/Toast';
import { adminDeleteComment } from '../../../contributor/api/admin';
import { ReactComponent as WarningIcon } from '../../../icons/icon-warning.svg';
import type { DeleteResponseStatusFragment } from '../../api/message';
import { AdminRemoveContent } from '../../Remove/AdminRemoveContent';

const messages = defineMessages({
  title: { defaultMessage: "Suppression modérateur d'un message", id: 'c7WI7d' },
  confirmationLabel: { defaultMessage: 'le message a été supprimé', id: 'AmLURL' },
  failureLabel: { defaultMessage: 'impossible de supprimer le message', id: 'LWM0Jj' },
  refuse: { defaultMessage: 'Oui, garder la discussion', id: 'zLk665' },
  remove: { defaultMessage: 'Non, supprimer toute la discussion', id: 'ovsC0d' },
  removeNoChild: { defaultMessage: 'Supprimer le commentaire', id: 'oJyvFI' },
  warning: {
    defaultMessage: `Ce commentaire possède des réponses qui seront supprimés avec lui.
  Souhaitez vous continuer à les afficher en remplaçant ce commentaire par “Ce commentaire a été retiré par le modérateur” ?`,
    id: '5eKNHE',
  },
  confirmDeleteNoChild: {
    defaultMessage: `Êtes-vous sûr de vouloir faire cela ?`,
    id: 'bbr3lo',
  },
  keep: {
    defaultMessage: `Annuler`,
    id: '8Kvk8a',
  },
});

export type AdminMessageRemoverProps = {
  messageId: string;
  threadId: string;
  websiteId: string;
  content: string;
  status: string;
  hasChildren: boolean;
  onRemove: (data: DeleteResponseStatusFragment) => void;
  asModerator?: boolean;
};
export const AdminMessageRemover: React.FC<PropsWithChildren<AdminMessageRemoverProps>> = ({
  messageId,
  threadId,
  websiteId,
  content,
  status,
  onRemove,
  hasChildren,
  children,
}) => {
  const remove = useCallback(
    async values => {
      try {
        const data = await adminDeleteComment({
          commentId: messageId,
          threadId,
          websiteId,
          ...values,
        });
        confirmationToast(<FormattedMessage {...messages.confirmationLabel} />);
        onRemove?.(data);
      } catch {
        failureToast(<FormattedMessage {...messages.failureLabel} />);
      }
    },
    [onRemove, messageId, threadId, websiteId],
  );

  const lazyContent = useCallback(
    tip => {
      const preserveDiscussion = (_: unknown, form: FormApi) => {
        form.change('preserveDiscussion', true);
      };
      const removeDiscussion = (_: unknown, form: FormApi) => {
        form.change('preserveDiscussion', false);
      };

      return (
        <AdminRemoveContent
          messages={messages}
          content={content}
          status={status}
          warning={
            hasChildren ? (
              <div className="flex space-x-3">
                <WarningIcon className="w-8 h-8 text-white bg-red-300 p-2 rounded mt-1.5" />
                <span className="flex-1">
                  <FormattedMessage {...messages.warning} />
                </span>
              </div>
            ) : (
              <FormattedMessage {...messages.confirmDeleteNoChild} />
            )
          }
          remove={async values => {
            await remove(values);
            tip.hide();
          }}
          cancel={tip.hide}
          actions={
            <div className="flex space-x-2 my-4">
              {hasChildren ? (
                <>
                  <SubmitButtonV3 onClick={preserveDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.refuse} />
                  </SubmitButtonV3>
                  <SubmitButtonV3 onClick={removeDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.remove} />
                  </SubmitButtonV3>
                </>
              ) : (
                <>
                  <ButtonV3 onClick={tip.hide} className="grow" variant={'text'}>
                    <FormattedMessage {...messages.keep} />
                  </ButtonV3>
                  <SubmitButtonV3 onClick={removeDiscussion} className="grow" variant="contained">
                    <FormattedMessage {...messages.removeNoChild} />
                  </SubmitButtonV3>
                </>
              )}
            </div>
          }
        />
      );
    },
    [remove, content, status, hasChildren],
  );

  return (
    <GraphModal maxWidth={800} lazyContent={lazyContent} showPageOverlay forceModal>
      {children}
    </GraphModal>
  );
};
